import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { CSSProperties, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Bag3Icon } from "@/shared/icons";
import { BaseIcon } from "@/shared/icons/BaseIcon";
import { Slider, SoundButton, SoundModal } from "@/shared/ui";
import { FRIDGE_CONTAINER } from "@/domains/estate/constants/constants";

type Props = {
  onClose: () => void;
  onPay: () => void;
  isPending: boolean;
  sliderValue: number;
  sliderValues: { label: string; value: number }[];
  onChangeSliderValue: (value: number) => void;
  paymentMethod: PaymentType;
  paymentMethods: { value: PaymentType; label: string; icon: typeof BaseIcon }[];
  onChangePaymentMethod: (value: PaymentType) => void;
  totalPrice: string;
};

export default function EstateFridgeDeliveryModal({
  onClose,
  onPay,
  isPending,
  sliderValue,
  sliderValues,
  onChangeSliderValue,
  paymentMethod,
  paymentMethods,
  onChangePaymentMethod,
  totalPrice,
}: Props) {
  const { t } = useTranslation("estate");
  const [activeTabBlockStyle, setActiveTabBlockStyle] = useState({ width: 0, left: 0 });
  const refs = useRef([]);

  useLayoutEffect(() => {
    const index = paymentMethods.findIndex((method) => method.value === paymentMethod);
    if (refs.current[index]) {
      const activeTabElement = refs.current[index];
      const { offsetWidth, offsetLeft } = activeTabElement;

      setActiveTabBlockStyle({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, [paymentMethod, paymentMethods]);

  const [min, max] = useMemo(() => {
    const values = sliderValues.map((item) => item.value);
    return [Math.min(...values), Math.max(...values)];
  }, [sliderValues]);

  const title = (
    <div className="flex items-center text-18 font-bold gap-2.5">
      <Bag3Icon className="size-5" />
      {t("estate.fridge.delivery")}
    </div>
  );

  return (
    <SoundModal
      open
      onClose={onClose}
      className="lightBlue2Shadow30 w-[25.125rem]"
      classNameOverlay="absolute"
      container={FRIDGE_CONTAINER}
      title={title}
      dataMzAccent="lightBlue2"
    >
      <div className="relative w-full h-[1px] bg-dark-800">
        <div className="absolute left-0 h-full w-[1rem] bg-accent-solid" />
      </div>
      <div className="mt-[1rem]">
        <div className="flex items-center justify-between">
          <div className="text-16 font-medium">{t("estate.fridge.countProducts")}:</div>
          <div className="text-14 text-accent-solid font-bold">{sliderValue}</div>
        </div>
        <Slider
          value={sliderValue}
          values={sliderValues}
          min={min}
          max={max}
          step={10}
          onChange={onChangeSliderValue}
          className="mt-3"
          classNameSlider="mt-3"
          classNameLabel="text-11 font-bold"
          styleSlider={{ "--custom-color": "var(--accent-solid)" } as CSSProperties}
        />
      </div>
      <div className="mt-5">
        <div className="text-16 font-medium">{t("estate.fridge.paymentMethod")}:</div>
        <div className="relative h-[2.75rem] grid grid-cols-2 w-full bg-dark-800 rounded-[0.5rem] mt-3.5">
          {paymentMethods.map((method, index) => (
            <button
              type="button"
              key={method.value}
              ref={(el) => {
                refs.current[index] = el;
                return el;
              }}
              className={clsx(
                "h-full px-5 hover:text-white transition-all duration-300 text-15 font-medium z-1",
                "flex items-center justify-center gap-2.5",
                method.value === paymentMethod ? "text-white" : "text-white/50",
              )}
              onClick={() => onChangePaymentMethod(method.value)}
            >
              <method.icon className="size-5" />
              {method.label}
            </button>
          ))}
          <div
            className="absolute h-full bg-gradient-accent rounded-[0.5rem] transition-all duration-300 z-0"
            style={{
              width: `${activeTabBlockStyle.width}px`,
              transform: `translateX(${activeTabBlockStyle.left}px)`,
            }}
          />
        </div>
      </div>
      <div className="mt-5">
        <div className="text-accent-solid text-18 font-bold">{totalPrice} ₽</div>
        <div className="text-15 font-medium text-white/50">Всего к оплате</div>
      </div>
      <SoundButton
        className="h-[2.75rem] w-full mt-3.5"
        loading={isPending}
        disabled={isPending}
        onClick={onPay}
      >
        Оплатить
      </SoundButton>
    </SoundModal>
  );
}
